<template>
  <BaseDialog :visible="visible" :config="getConfig" @close="handleClose()" @action="selectAssignment(selectedAssignmentEdited)"
    :isValidForSubmit="isValidForSubmit">
    <div class="assignment-dialog-body">
      <div class="search-row">
        <v-text-field
          v-model="searchText"
          class="search-input"
          placeholder="Search"
          variant="outlined"
          :append-inner-icon="'mdi-magnify'"
          hide-details
          density="compact"
        ></v-text-field>
      </div>
      <!-- <div v-if="isLoading && assignments.length === 0" class="text-center text-gray-500 loading-container">
        <IconProvider name="Loader" class="loader animate-spin" />
        <div class="loading-text">Loading Assignments</div>
      </div>
      <div v-if="!isLoading && assignments.length == 0" class="text-center text-gray-500">
        <div class="loading-text">No Assignments Found</div>
      </div> -->
      <div
        class="assignment-list"
        v-if="assignments.length > 0"
        @scroll="handleScroll"
        ref="assignmentList"
      >
        <AssignmentCard
          v-for="assignment in assignments"
          :key="assignment.id"
          :assignment="assignment"
          :selectedAssignment="selectedAssignmentEdited"
          :isFromEmail="assignment.id === emailExtractedAssignmentId"
          @select="handleAssignmentSelection(assignment)"
        />
      </div>
      <div v-if="isLoadingMore" class="loading-more-container">
          <IconProvider name="Loader" class="loader animate-spin" />
          <div class="loading-text">Loading assignments...</div>
        </div>
        
        <!-- End of results indicator -->
        <div v-if="hasReachedEnd && !isLoadingMore" class="end-of-results">
          <div class="loading-text">No more assignments to load</div>
        </div>
    </div>
  </BaseDialog>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import AssignmentCard from "./AssignmentCard.vue";
import BaseDialog from "../../commons/BaseDialog.vue";
import IconProvider from "../../commons/IconProvider.vue";
import { searchAssignmentsFromEmail } from "../../../service/AssignmentSearchService.js";
import config from "../config.json";
import { EventBus } from "../../../service/event-bus";

export default {
  name: "AssignmentDialog",
  components: { BaseDialog, AssignmentCard, IconProvider },
  props: {
    visible: Boolean,
    loadAssignments: Boolean
  },
  data() {
    return {
      searchText: "",
      isAssignmentSelected: false,
      selectedAssignmentEdited: null,
      config: config,
      // Pagination state
      currentPage: 1,
      pageSize: 50,
      isLoadingMore: false,
      hasReachedEnd: false,
      searchDebounceTimer: null,
      emailExtractedAssignmentId: null, // Track which assignment was extracted from email
      manualSelectionMade: false // Track if user manually selected an assignment
    };
  },
  updated() {
    // Only sync with store selection if no manual selection has been made
    if (!this.manualSelectionMade && this.selectedAssignment !== this.selectedAssignmentEdited) {
      this.selectedAssignmentEdited = this.selectedAssignment;
    }
  },
  created() {
    if(this.loadAssignments) {
      this.initializeAssignments();
    }
    this.selectedAssignmentEdited = this.selectedAssignment;
  },
  computed: {
    ...mapGetters({
      isLoading: "assignments/isLoading",
      assignments: "assignments/getAssignments", // Get all loaded assignments
      selectedAssignment: "assignments/selectedAssignment"
    }),
    getConfig() {
      return this.config.selectAssignment || {};
    },
    isValidForSubmit() {
      const isValid = !!this.selectedAssignmentEdited;
      return isValid;
    }
  },
  watch: {
    searchText() {
      this.debouncedSearch();
    },
    selectedAssignmentEdited: {
      handler(newVal, oldVal) {
        this.$forceUpdate();
      },
      immediate: true
    },
    async visible(newValue, oldValue) {
      // When dialog becomes visible, ensure assignments are loaded
      if (newValue && !oldValue && this.loadAssignments) {
        console.log('Assignment dialog opened, loading assignments...');
        try {
          await this.initializeAssignments();
        } catch (error) {
          console.error('Error loading assignments on dialog open:', error);
        }
      }
      
      // Reset manual selection flag and clear selection when dialog closes
      if (!newValue && oldValue) {
        this.manualSelectionMade = false;
        this.selectedAssignmentEdited = null;
        this.emailExtractedAssignmentId = null; // Clear email extraction indicator
      }
    }
  },
  methods: {
    ...mapActions({
      fetchAssignments: "assignments/fetchAssignments",// Add this action to your store
      fetchAssignmentChecklist: "assignments/fetchAssignmentChecklist",
    }),
    
    async extractAndPrioritizeFromEmail() {
      try {
        console.log('🔍 Starting hierarchical search from current email...');
        
        // First, get current email data using the existing util
        const { getCurrentEmailData } = await import('../../../utils/emailAssignmentExtractor');
        const emailData = await getCurrentEmailData();
        
        if (!emailData) {
          console.warn('No email data available for search');
          return null;
        }

        console.log('📧 Email data extracted:', {
          subject: emailData.subject?.substring(0, 50) + '...',
          bodyLength: emailData.body?.length || 0
        });

        // Use the comprehensive search service with email data
        const searchResult = await searchAssignmentsFromEmail(emailData, {
          maxResults: 20,
          includeScoring: true,
          fallbackToFuzzy: true
        });
        
        if (searchResult.assignments && searchResult.assignments.length > 0) {
          const topAssignment = searchResult.assignments[0];
         
          
          // Mark this assignment as extracted from email for visual highlighting
          this.emailExtractedAssignmentId = topAssignment.id;
          
          // Update assignments list with search results and existing data
          const mergedAssignments = this.mergeAndPrioritizeAssignments(
            searchResult.assignments, 
            this.assignments || []
          );
          this.$store.commit('assignments/SET_ASSIGNMENTS', mergedAssignments);
          
          // Show success notification with strategy info
          const strategyNames = {
            'FILE_ID': 'File ID match',
            'ASSIGNMENT_ID': 'Assignment ID match',
            'PROPERTY_NAME': 'Property name match',
            'PROPERTY_ADDRESS': 'Address match', 
            'CLIENT_DETAILS': 'Client details match'
          };
          
          this.$emit('showNotification', {
            type: 'success',
            message: `Found assignment ${topAssignment.code} via ${strategyNames[searchResult.searchStrategy] || searchResult.searchStrategy}. Click to select.`
          });

          // Remove auto-selection - let user manually select
          // if (topAssignment.searchScore && topAssignment.searchScore > 0.8) {
          //   this.handleAssignmentSelection(topAssignment);
          //   console.log('🎯 Auto-selected high-confidence match:', topAssignment.code);
          // }
          
          return topAssignment;
        } else {
          console.log('❌ No assignment found via hierarchical search');
          
          // Show the details of what was searched
          if (searchResult.searchContext) {
            console.log('📋 Search context:', searchResult.searchContext);
          }
          
          return null;
        }
      } catch (error) {
        console.error('❌ Error in hierarchical search from email:', error);
        this.$emit('showNotification', {
          type: 'error',
          message: 'Error searching for assignment from email content'
        });
        return null;
      }
    },
    debouncedSearch() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }

      // Set new timer
      this.searchDebounceTimer = setTimeout(() => {
        this.$store.dispatch('assignments/resetAssignments');
        this.initializeAssignments();
      }, 500);
    },


    async initializeAssignments() {
      try {
        console.log('🔄 Initializing assignments...');
        this.currentPage = 1;
        this.hasReachedEnd = false;
        this.isLoadingMore = true;
        
        // First, load regular assignments
        await this.fetchAssignments({
          searchText: this.searchText?.trim(),
          page: this.currentPage,
          pageSize: this.pageSize,
          reset: true // Clear existing assignments
        });
        
        // Then, try to extract and prioritize assignment from current email
        if (!this.searchText?.trim()) { // Only if not manually searching
          await this.extractAndPrioritizeFromEmail();
        }
        
        console.log('✅ Assignments initialized successfully');
        this.isLoadingMore = false;
      } catch (error) {
        console.error('❌ Error initializing assignments:', error);
        this.isLoadingMore = false;
      }
    },

    async handleSearchChange(newValue, oldValue) {
      if (newValue !== oldValue) {
        // Clear email extraction indicator when manually searching
        this.emailExtractedAssignmentId = null;
        
        // Reset pagination for searchText
        this.currentPage = 1;
        this.hasReachedEnd = false;
        
        if (newValue && newValue.trim()) {
          // Perform search using fetchAssignments
          try {
            await this.fetchAssignments({
              searchText: newValue.trim(),
              page: this.currentPage,
              pageSize: this.pageSize,
              reset: true
            });
          } catch (error) {
            console.error('Error searching assignments:', error);
          }
        } else {
          // Load regular assignments when search is cleared
          await this.initializeAssignments();
        }
      }
    },

    async handleScroll(event) {
      const element = event.target;
      const threshold = 50; // Pixels from bottom to trigger load
      
      // Check if scrolled near the bottom
      if (element.scrollHeight - element.scrollTop <= element.clientHeight + threshold) {
        await this.loadMore();
      }
    },

    async loadMore() {
      // Prevent multiple simultaneous loads
      if (this.isLoadingMore || this.hasReachedEnd || this.isLoading) {
        return;
      }

      try {
        this.isLoadingMore = true;
        this.currentPage += 1;

        let result = await this.fetchAssignments({
            page: this.currentPage,
            pageSize: this.pageSize,
            searchText: this.searchText?.trim(),
            reset: false // Append to existing results
          });

        // Check if we've reached the end
        if (!result || result.length < this.pageSize) {
          this.hasReachedEnd = true;
        }
      } catch (error) {
        console.error('Error loading more assignments:', error);
        this.currentPage -= 1; // Revert page increment on error
      } finally {
        this.isLoadingMore = false;
      }
    },

    handleClose() {
      this.selectedAssignmentEdited = null;
      this.searchText = ""; 
      this.isAssignmentSelected = false;
      this.manualSelectionMade = false; // Reset manual selection flag
      this.$emit("close");
    },

    async handleAssignmentSelection(assignment) {
      this.manualSelectionMade = true;
      // Set the selected assignment
      this.selectedAssignmentEdited = assignment;
      this.fetchAssignmentChecklist(assignment?.viewEventId)
        .then(checklist => {
          console.log('📋 Fetched Checklist:', checklist);
        })
        .catch(error => {
          console.warn('⚠️ Failed to fetch assignment checklist:', error);
        });
    },

    selectAssignment(assignment) {
      this.$emit("selectAssignment", assignment);
      this.isAssignmentSelected = true; 
      this.config.selectAssignment.showClearButton = true;
      EventBus.emit('toggleRecentActionFilter', 'assignment');
      this.$emit("close");
    },

    async refreshAssignments() {
      // Reset everything and reload
      this.currentPage = 1;
      this.hasReachedEnd = false;
      await this.initializeAssignments();
      // this.$emit("refresh");
    },

    deselectAssignment() {
      this.$emit("selectAssignment", null);
      this.isAssignmentSelected = false;
      this.config.selectAssignment.showClearButton = false;
    },

    mergeAndPrioritizeAssignments(searchResults, currentAssignments) {
      // Create a map of existing assignments for quick lookup
      const existingMap = new Map();
      currentAssignments.forEach(assignment => {
        existingMap.set(assignment.id, assignment);
      });

      const mergedAssignments = [];
      const addedIds = new Set();

      // First, add search results (prioritized)
      searchResults.forEach(searchResult => {
        const existing = existingMap.get(searchResult.id);
        mergedAssignments.push({
          ...existing,
          ...searchResult,
          // Preserve any existing data but override with search data
          searchScore: searchResult.searchScore,
          searchStrategy: searchResult.searchStrategy
        });
        addedIds.add(searchResult.id);
      });

      // Then add remaining current assignments that weren't in search results
      currentAssignments.forEach(assignment => {
        if (!addedIds.has(assignment.id)) {
          mergedAssignments.push(assignment);
        }
      });

      return mergedAssignments;
    },
  },
};
</script>

<style scoped>
.search-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 22px 0 8px;
}

.refresh-btn {
  border: 1px solid rgb(196 200 205 / var(--tw-border-opacity, 1));
  cursor: pointer;
  padding: 0.6rem;
  border-radius: 4px;
  color: gray !important;
  cursor: pointer;
  width: 36px;
  height: 36px;
}

.refresh-btn:hover {
  background: #f0f0f0;
}

.assignment-list {
  overflow-y: auto;
  overflow-x: hidden;
  max-height: 340px;
  padding: 3px 8px 8px 8px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  scroll-behavior: smooth;
}

.loading-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.loading-more-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 0px;
  padding: 0 15px;
  /* border-top: 1px solid #d5d2d2; */
}

.end-of-results {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 15px;
  color: #666;
  font-style: italic;
}

.loading-text {
  /* margin-top: 10px; */
  font-size: 15px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.clear-assignment {
  margin-top: 1rem;
  color: rgb(239 68 68 / var(--tw-text-opacity, 1));
  cursor: pointer;
  text-align: end;
}

.clear-assignment:hover {
  text-decoration: underline;
  color: darkred;
}
</style>